import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import api from '../config/axios';
import DevOtpInbox from '../components/DevOtpInbox';
import PageHeader from '../components/ui/PageHeader';
import SectionCard from '../components/ui/SectionCard';
import { canReviewApprovals, readSessionUser } from '../utils/sessionUser';
import { filterParticipants } from '../utils/participantFilters';
import { formatLocalTimestamp as formatWhen } from '../utils/hackathonFlow';

const kindLabel = (kind) => (kind === 'student' ? 'Aluno' : kind === 'external' ? 'Externo' : kind);
const enrollmentLabel = (status) => (status === 'active' ? 'Inscrito' : status === 'cancelled' ? 'Cancelada' : status || '—');

const formatDay = (value) => {
  const matched = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return matched ? `${matched[3]}/${matched[2]}/${matched[1]}` : value;
};

const presenceLabel = (row) => {
  if (Array.isArray(row.days) && row.days.length > 0) {
    return row.days.map((day) => {
      const entry = day.checkInAt ? `entrada ${formatWhen(day.checkInAt)}` : 'sem entrada';
      const exit = day.checkOutAt ? `saída ${formatWhen(day.checkOutAt)}` : 'sem saída';
      return `${formatDay(day.day)}: ${entry}, ${exit}`;
    }).join(' · ');
  }
  if (row.checkOutAt) return `Saída em ${formatWhen(row.checkOutAt)}`;
  if (row.checkInAt) return `Entrada em ${formatWhen(row.checkInAt)}`;
  return 'Sem presença registrada';
};

export default function ParticipantsPage() {
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [activities, setActivities] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [query, setQuery] = useState('');
  const [activityId, setActivityId] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const eventId = params.get('evento') || '';
  const showDevInbox = canReviewApprovals(readSessionUser());

  useEffect(() => {
    api.get('/events')
      .then((response) => setEvents(response.data || []))
      .catch(() => setError('Não foi possível carregar os eventos.'));
  }, []);

  useEffect(() => {
    if (!eventId) {
      setActivities([]);
      setParticipants([]);
      setLoading(false);
      return;
    }
    setError('');
    setLoading(true);
    api.get(`/events/${eventId}/sub-events`)
      .then((response) => setActivities(response.data || []))
      .catch(() => setActivities([]));
    api.get(`/hackathon/admin/events/${eventId}/participants`)
      .then((response) => setParticipants(response.data?.participants || []))
      .catch(() => setError('Não foi possível carregar os participantes.'))
      .finally(() => setLoading(false));
  }, [eventId, reloadKey]);

  const visible = useMemo(
    () => filterParticipants(participants, { query, activityId, status }),
    [participants, query, activityId, status]
  );

  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <PageHeader
        title="Participantes"
        subtitle="Inscritos do hackathon neste evento. Eles não são usuários do PontoNow."
      />
      {showDevInbox ? <DevOtpInbox /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      <SectionCard>
        <Stack spacing={2}>
          <TextField
            select
            label="Evento"
            value={eventId}
            onChange={(event) => {
              const next = new URLSearchParams(params);
              if (event.target.value) next.set('evento', event.target.value);
              else next.delete('evento');
              setParams(next);
              setActivityId('');
            }}
          >
            <MenuItem value="">Selecione um evento</MenuItem>
            {events.map((event) => <MenuItem key={event.id} value={String(event.id)}>{event.nome}</MenuItem>)}
          </TextField>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              label="Nome, identificador ou e-mail"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              fullWidth
            />
            <TextField select label="Atividade" value={activityId} onChange={(event) => setActivityId(event.target.value)} fullWidth>
              <MenuItem value="">Todas</MenuItem>
              {activities.map((activity) => <MenuItem key={activity.id} value={String(activity.id)}>{activity.nome}</MenuItem>)}
            </TextField>
            <TextField select label="Situação" value={status} onChange={(event) => setStatus(event.target.value)} fullWidth>
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="active">Inscrição ativa</MenuItem>
              <MenuItem value="cancelled">Inscrição cancelada</MenuItem>
              <MenuItem value="present">Com entrada</MenuItem>
              <MenuItem value="absent">Sem presença</MenuItem>
              <MenuItem value="checked_out">Com saída</MenuItem>
            </TextField>
          </Stack>
          {!eventId ? (
            <Typography color="text.secondary">Escolha um evento para ver os inscritos.</Typography>
          ) : loading ? (
            <Typography color="text.secondary">Carregando participantes.</Typography>
          ) : visible.length === 0 ? (
            <Typography color="text.secondary">Nenhum participante encontrado.</Typography>
          ) : visible.map((row) => (
            <Stack
              key={row.attendeeId}
              spacing={0.5}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, minWidth: 0 }}
            >
              <Typography variant="h6">{row.nome}</Typography>
              <Typography color="text.secondary">{kindLabel(row.kind)} · {row.identificador || 'Sem identificador'}</Typography>
              <Typography sx={{ overflowWrap: 'anywhere' }}>{row.email}</Typography>
              <Typography>Inscrição: {enrollmentLabel(row.enrollmentStatus)} · {formatWhen(row.enrolledAt)}</Typography>
              <Typography>
                {row.emailVerified ? 'E-mail verificado' : 'E-mail ainda não verificado'}
                {' · '}
                {row.sessionValid ? 'Sessão ativa' : 'Sem sessão ativa'}
              </Typography>
              <Typography>Presença no evento: {presenceLabel(row)}</Typography>
              <Typography>
                Atividades: {(row.activities || []).length === 0
                  ? 'Nenhuma'
                  : row.activities.map((activity) => `${activity.nome} (${enrollmentLabel(activity.status)})`).join(', ')}
              </Typography>
            </Stack>
          ))}
          {eventId ? (
            <Button variant="outlined" onClick={() => setReloadKey((value) => value + 1)} sx={{ alignSelf: 'flex-start' }}>
              Atualizar lista
            </Button>
          ) : null}
        </Stack>
      </SectionCard>
    </Stack>
  );
}
